import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useGetPlatformHealthDetailQuery } from 'src/redux/operations/operations.endpoints';
import { VerificationStatusBadge } from './verification-status-badge';

type Props = {
  platform: string;
  checkDate: string;
};

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function PlatformHealthDetail({ platform, checkDate }: Props) {
  const { data, isLoading, isError } = useGetPlatformHealthDetailQuery(
    { platform, date: checkDate },
    { skip: !platform || !checkDate }
  );

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 py-4 text-sm text-gray-600"
        role="status"
      >
        <Loader2 className="size-4 animate-spin" />
        Loading check details…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        role="alert"
      >
        Could not load check details for this platform.
      </div>
    );
  }

  const reconciliationChecks = data?.reconciliation_checks ?? [];

  return (
    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
      <h3 className="mb-2 text-sm font-semibold text-gray-900">
        Reconciliation checks
      </h3>
      {reconciliationChecks.length === 0 ? (
        <p className="text-sm text-gray-600">
          No reconciliation checks for this date.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                Account ID
              </TableHead>
              <TableHead>
                Account
              </TableHead>
              <TableHead>
                Metric
              </TableHead>
              <TableHead className="text-right">
                Vendor total
              </TableHead>
              <TableHead className="text-right">
                Mart total
              </TableHead>
              <TableHead className="text-right">
                Delta %
              </TableHead>
              <TableHead>
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reconciliationChecks.map((row) => (
              <TableRow key={`${row.account_id}-${row.metric}`}>
                <TableCell className="font-mono text-xs">
                  {row.account_id}
                </TableCell>
                <TableCell>
                  {row.account_name}
                </TableCell>
                <TableCell>
                  {row.metric}
                </TableCell>
                <TableCell className="text-right">
                  {formatNumber(row.vendor_total)}
                </TableCell>
                <TableCell className="text-right">
                  {formatNumber(row.mart_total)}
                </TableCell>
                <TableCell className="text-right">
                  {formatNumber(row.delta_pct)}
                </TableCell>
                <TableCell>
                  <VerificationStatusBadge status={row.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
